/**
 * ActiveTab Component
 *
 * Displays currently active layers with reorder and opacity controls.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt,
  faPowerOff,
  faSave,
  faFolderOpen,
  faCheckSquare,
  faTimes,
  faToggleOn,
  faToggleOff,
  faCheckDouble,
  faShareNodes
} from '@fortawesome/free-solid-svg-icons';
import './ActiveTab.scss';
import { useLayerDrawer, LiveLayerDef } from '../LayerDrawerContext';
import { Layer } from '../../../types/layer';
import { LayerCard } from './LayerCard';
import { LiveLayerCard } from './LiveLayerCard';

type LingeringLayer = Layer & { isLingering: boolean };
type LingeringLiveLayer = LiveLayerDef & { isLingering: boolean };

/**
 * Tiny SVG analog-clock-style countdown — the ring starts full and drains
 * around the circle over the linger window, so users can see how long
 * they have to re-enable a lingering row before it disappears.
 *
 * Uses SMIL <animate> rather than CSS keyframes because CSS animations
 * on SVG attributes can fail to apply when the parent has filter/opacity
 * applied (which our lingering wrapper does). SMIL renders the animation
 * inside the SVG engine, fully decoupled from the CSS layer.
 */
const LingerClock: React.FC<{ tone?: 'success' | 'primary' }> = ({ tone = 'primary' }) => {
  // 2 * π * r where r=10 → ≈ 62.832; the dasharray equals the circumference
  // and we animate dashoffset 0 → C so the visible stroke drains to zero.
  const C = 62.832;
  return (
    <svg className={`linger-clock linger-clock--${tone}`} viewBox="0 0 24 24" aria-hidden="true">
      <circle
        cx="12"
        cy="12"
        r="10"
        className="linger-clock__fill"
        strokeDasharray={C}
      >
        <animate
          attributeName="stroke-dashoffset"
          from="0"
          to={C}
          dur="5s"
          fill="freeze"
        />
      </circle>
    </svg>
  );
};
import { useDragReorder } from '../hooks/useDragReorder';
import { sortLayersByOrder } from '../utils/layerDrawerUtils';
import { SavePresetModal } from '../modals/SavePresetModal';
import { LoadPresetModal } from '../modals/LoadPresetModal';
import { LayerPreset } from '../../../types/layerPreset';
import { SavedMapModal } from '../../shareMap/SavedMapModal';
import { useLayerKeyboardShortcuts } from '../hooks/useLayerKeyboardShortcuts';
import { usePermissions } from '../../../context/PermissionContext';

export const ActiveTab: React.FC = () => {
  const {
    layers,
    loading,
    error,
    reorderLayers,
    toggleLayer,
    liveLayers,
    toggleLiveLayer,
    loadPreset,
    // Batch selection
    selectionMode,
    selectedLayerIds,
    toggleSelectionMode,
    selectAllLayers,
    deselectAllLayers,
    enableSelectedLayers,
    disableSelectedLayers
  } = useLayerDrawer();
  const [disablingAll, setDisablingAll] = useState(false);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [showLoadPresetModal, setShowLoadPresetModal] = useState(false);
  const [batchOperating, setBatchOperating] = useState(false);
  // #557 — the saved map + permalink, shown in the post-save confirmation modal
  // (null = closed). Populated after a successful save via Save or Share.
  const [savedResult, setSavedResult] = useState<{ url: string; name: string } | null>(null);
  const { isAdmin } = usePermissions();

  // ── Lingering recently-disabled layers ────────────────────────────────────
  // Keep a layer visible in the Active list for 30s after the user toggles
  // it off, so they can quickly flip it back on (typical use: "blink the
  // perimeter to see where it is on the map"). Re-toggling on within the
  // window cancels the timer.
  const LINGER_MS = 5_000;
  const [lingering, setLingering] = useState<Set<string>>(new Set());
  const prevEnabledRef = useRef<Set<string>>(new Set());
  const lingerTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const currentlyEnabled = new Set<string>();
    for (const l of layers) if (l.enabled) currentlyEnabled.add(l.id);
    for (const l of liveLayers) if (l.enabled) currentlyEnabled.add(l.id);

    const prev = prevEnabledRef.current;
    const justDisabled = new Set<string>();
    const justEnabled = new Set<string>();
    prev.forEach(id => { if (!currentlyEnabled.has(id)) justDisabled.add(id); });
    currentlyEnabled.forEach(id => { if (!prev.has(id)) justEnabled.add(id); });

    if (justDisabled.size === 0 && justEnabled.size === 0) {
      prevEnabledRef.current = currentlyEnabled;
      return;
    }

    setLingering(prevLingering => {
      const next = new Set(prevLingering);
      justEnabled.forEach(id => {
        // Re-enabled within the linger window — cancel the timer.
        const t = lingerTimersRef.current.get(id);
        if (t) { clearTimeout(t); lingerTimersRef.current.delete(id); }
        next.delete(id);
      });
      justDisabled.forEach(id => {
        // Cancel any prior timer for this id (rapid double-toggle), then
        // start a fresh 30s countdown.
        const existing = lingerTimersRef.current.get(id);
        if (existing) clearTimeout(existing);
        next.add(id);
        const t = setTimeout(() => {
          lingerTimersRef.current.delete(id);
          setLingering(curr => {
            if (!curr.has(id)) return curr;
            const m = new Set(curr);
            m.delete(id);
            return m;
          });
        }, LINGER_MS);
        lingerTimersRef.current.set(id, t);
      });
      return next;
    });

    prevEnabledRef.current = currentlyEnabled;
  }, [layers, liveLayers]);

  useEffect(() => {
    const timers = lingerTimersRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // Keyboard shortcuts
  useLayerKeyboardShortcuts({
    onSavePreset: () => setShowSavePresetModal(true),
    onLoadPreset: () => setShowLoadPresetModal(true)
  });

  // Get enabled overlay layers sorted by order (highest order = top of list = top of map).
  // Lingering recently-disabled overlays are included so the user can re-enable them
  // within a short window — flagged via isLingering for visual treatment / drag exclusion.
  const enabledOverlays = useMemo<LingeringLayer[]>(() => {
    const overlays: LingeringLayer[] = layers
      .filter(layer => layer.category !== 'backgrounds' && (layer.enabled || lingering.has(layer.id)))
      .map(layer => ({ ...layer, isLingering: !layer.enabled && lingering.has(layer.id) }));
    return sortLayersByOrder(overlays) as LingeringLayer[];
  }, [layers, lingering]);

  // Get enabled background layer (should only be one). Backgrounds don't linger
  // because there's always exactly one selected.
  const enabledBackground = useMemo(() => {
    return layers.find(layer => layer.enabled && layer.category === 'backgrounds');
  }, [layers]);

  // Get enabled (and lingering) live layers
  const enabledLiveLayers = useMemo<LingeringLiveLayer[]>(() => {
    return liveLayers
      .filter(layer => layer.enabled || lingering.has(layer.id))
      .map(layer => ({ ...layer, isLingering: !layer.enabled && lingering.has(layer.id) }));
  }, [liveLayers, lingering]);

  // Only truly-enabled (not lingering) overlay IDs for drag reordering — dragging
  // a half-disabled card would be confusing.
  const enabledLayerIds = useMemo(() => {
    return enabledOverlays.filter(l => !l.isLingering).map(layer => layer.id);
  }, [enabledOverlays]);

  // Total count of active layers (overlays + background + live) — only count
  // truly-enabled ones so the badge doesn't include lingering rows.
  const totalActiveCount =
    enabledOverlays.filter(l => !l.isLingering).length +
    (enabledBackground ? 1 : 0) +
    enabledLiveLayers.filter(l => !l.isLingering).length;

  /**
   * Handle reorder
   */
  const handleReorder = useCallback(async (reorderedIds: string[]) => {
    try {
      await reorderLayers(reorderedIds);
    } catch (err) {
      console.error('Failed to reorder layers:', err);
    }
  }, [reorderLayers]);

  /**
   * Handle preset selection from LoadPresetModal
   */
  const handlePresetSelected = useCallback(async (preset: LayerPreset) => {
    await loadPreset(preset);
  }, [loadPreset]);

  /**
   * #557 — Save and Share are one flow: both open the create dialog (which
   * names the map and keeps the global option). On a successful save the map's
   * permalink is already copied to the clipboard; we show the confirmation modal.
   */
  const handleSaved = useCallback((result?: { preset: LayerPreset; url: string }) => {
    if (result) {
      setSavedResult({ url: result.url, name: result.preset.name });
    }
  }, []);

  /**
   * Handle batch enable selected layers
   */
  const handleBatchEnable = useCallback(async () => {
    setBatchOperating(true);
    try {
      await enableSelectedLayers();
    } catch (err) {
      console.error('Failed to enable selected layers:', err);
    } finally {
      setBatchOperating(false);
    }
  }, [enableSelectedLayers]);

  /**
   * Handle batch disable selected layers
   */
  const handleBatchDisable = useCallback(async () => {
    setBatchOperating(true);
    try {
      await disableSelectedLayers();
    } catch (err) {
      console.error('Failed to disable selected layers:', err);
    } finally {
      setBatchOperating(false);
    }
  }, [disableSelectedLayers]);

  /**
   * Turn off all active layers (overlays and live layers, NOT background)
   */
  const handleAllLayersOff = useCallback(async () => {
    setDisablingAll(true);
    try {
      // Disable all enabled overlay layers (not background - always need one).
      // Skip lingering rows — they're already disabled; toggling would re-enable them.
      for (const layer of enabledOverlays) {
        if (layer.isLingering) continue;
        await toggleLayer(layer.id);
      }
      for (const liveLayer of enabledLiveLayers) {
        if (liveLayer.isLingering) continue;
        toggleLiveLayer(liveLayer.id);
      }
    } catch (err) {
      console.error('Failed to disable all layers:', err);
    } finally {
      setDisablingAll(false);
    }
  }, [enabledOverlays, enabledLiveLayers, toggleLayer, toggleLiveLayer]);

  /**
   * Drag reorder hook
   */
  const {
    dragState,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleTouchMove,
    getDragStyles
  } = useDragReorder({
    onReorder: handleReorder,
    itemIds: enabledLayerIds
  });

  /**
   * Handle layer card drag events
   */
  const onLayerDragStart = useCallback((layerId: string) => {
    handleDragStart(layerId);
  }, [handleDragStart]);

  if (loading) {
    return (
      <div className="active-tab">
        <div className="active-tab-loading">
          <div className="loading-spinner"><FontAwesomeIcon icon={faSyncAlt} spin /></div>
          <div className="loading-text">Loading layers...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="active-tab">
        <div className="active-tab-error">
          <div className="error-icon">⚠</div>
          <div className="error-text">{error}</div>
        </div>
      </div>
    );
  }

  if (totalActiveCount === 0) {
    return (
      <div className="active-tab">
        <div className="active-tab-empty">
          <div className="empty-icon">📋</div>
          <div className="empty-text">No active layers</div>
          <div className="empty-hint">Enable layers from the Browse tab or load a preset</div>
          <div className="empty-actions">
            <button
              className="btn-secondary preset-btn"
              onClick={() => setShowLoadPresetModal(true)}
              title="Load a saved preset"
            >
              <FontAwesomeIcon icon={faFolderOpen} />
              <span>Load Preset</span>
            </button>
          </div>
        </div>

        {/* Load Preset Modal - available even with no layers */}
        {showLoadPresetModal && (
          <LoadPresetModal
            isOpen={true}
            onClose={() => setShowLoadPresetModal(false)}
            onPresetSelected={handlePresetSelected}
          />
        )}
      </div>
    );
  }

  return (
    <div className="active-tab">
      <div className="active-tab-header">
        <div className="active-tab-title">Active Layers</div>
        <div className="active-tab-count">{totalActiveCount} layer{totalActiveCount !== 1 ? 's' : ''}</div>
      </div>

      {totalActiveCount > 0 && (
        <div className="active-tab-actions">
          {!selectionMode ? (
            <>
              <button
                className="all-layers-off-btn"
                onClick={handleAllLayersOff}
                disabled={disablingAll}
              >
                <FontAwesomeIcon icon={faPowerOff} />
                <span>{disablingAll ? 'Turning off...' : 'All Layers Off'}</span>
              </button>
              <div className="preset-controls">
                <button
                  className="btn-primary preset-btn"
                  onClick={() => setShowSavePresetModal(true)}
                  title="Save current layer configuration as a preset"
                >
                  <FontAwesomeIcon icon={faSave} />
                  <span>Save</span>
                </button>
                <button
                  className="btn-secondary preset-btn"
                  onClick={() => setShowLoadPresetModal(true)}
                  title="Load a saved preset"
                >
                  <FontAwesomeIcon icon={faFolderOpen} />
                  <span>Load</span>
                </button>
                <button
                  className="btn-neutral preset-btn"
                  onClick={toggleSelectionMode}
                  title="Select multiple layers for batch operations"
                >
                  <FontAwesomeIcon icon={faCheckSquare} />
                  <span>Select</span>
                </button>
                <button
                  className="btn-neutral preset-btn"
                  onClick={() => setShowSavePresetModal(true)}
                  title="Save this map and get a shareable link"
                >
                  <FontAwesomeIcon icon={faShareNodes} />
                  <span>Share</span>
                </button>
              </div>
            </>
          ) : (
            <div className="batch-controls">
              <div className="batch-controls-header">
                <span className="batch-selection-count">
                  {selectedLayerIds.size} selected
                </span>
                <button
                  className="batch-cancel-btn"
                  onClick={toggleSelectionMode}
                  title="Cancel selection"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div className="batch-actions">
                <button
                  className="btn-neutral batch-action-btn"
                  onClick={selectAllLayers}
                  title="Select all layers"
                >
                  <FontAwesomeIcon icon={faCheckDouble} />
                  <span>All</span>
                </button>
                <button
                  className="btn-neutral batch-action-btn"
                  onClick={deselectAllLayers}
                  title="Clear selection"
                  disabled={selectedLayerIds.size === 0}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  <span>Clear</span>
                </button>
                <button
                  className="btn-neutral batch-action-btn"
                  onClick={handleBatchEnable}
                  disabled={selectedLayerIds.size === 0 || batchOperating}
                  title="Enable selected layers"
                >
                  <FontAwesomeIcon icon={faToggleOn} />
                  <span>Enable</span>
                </button>
                <button
                  className="btn-neutral batch-action-btn"
                  onClick={handleBatchDisable}
                  disabled={selectedLayerIds.size === 0 || batchOperating}
                  title="Disable selected layers"
                >
                  <FontAwesomeIcon icon={faToggleOff} />
                  <span>Disable</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="active-tab-hint">
        Drag to reorder • Top layer appears on top of map
      </div>

      <div
        className="active-tab-list"
        onTouchMove={handleTouchMove}
      >
        {/* Live Layers Section - Show at top (above all map layers) */}
        {enabledLiveLayers.length > 0 && (
          <div className="active-tab-live-section">
            <div className="active-tab-section-label">Live Data</div>
            {enabledLiveLayers.map(liveLayer => (
              <div
                key={liveLayer.id}
                className={liveLayer.isLingering ? 'layer-row-lingering' : undefined}
              >
                <div className={liveLayer.isLingering ? 'layer-row-lingering__content' : undefined}>
                  <LiveLayerCard
                    liveLayer={liveLayer}
                    onToggle={() => toggleLiveLayer(liveLayer.id)}
                  />
                </div>
                {liveLayer.isLingering && <LingerClock tone="success" />}
              </div>
            ))}
          </div>
        )}

        {/* Overlay Layers Section - Sorted with highest order at top */}
        {enabledOverlays.length > 0 && (
          <>
            <div className="active-tab-section-label">Overlays</div>
            {enabledOverlays.map(layer => (
              <div
                key={layer.id}
                data-layer-id={layer.id}
                className={layer.isLingering ? 'layer-row-lingering' : undefined}
                style={layer.isLingering ? undefined : getDragStyles(layer.id)}
                onMouseEnter={() => !layer.isLingering && handleDragOver(layer.id)}
                onMouseUp={handleDragEnd}
                onTouchEnd={handleDragEnd}
              >
                <div className={layer.isLingering ? 'layer-row-lingering__content' : undefined}>
                  <LayerCard
                    layer={layer}
                    showOpacity={true}
                    showInfo={true}
                    showDragHandle={!layer.isLingering}
                    onDragStart={layer.isLingering ? undefined : onLayerDragStart}
                    onDragEnd={handleDragEnd}
                  />
                </div>
                {layer.isLingering && <LingerClock tone="primary" />}
              </div>
            ))}
          </>
        )}

        {/* Background Layer Section - Always at bottom */}
        {enabledBackground && (
          <>
            <div className="active-tab-section-label">Background</div>
            <LayerCard
              layer={enabledBackground}
              showOpacity={false}
              showInfo={true}
              showDragHandle={false}
            />
          </>
        )}
      </div>

      {dragState.isDragging && (
        <div className="drag-overlay" />
      )}

      {/* Preset Modals */}
      {showSavePresetModal && (
        <SavePresetModal
          isOpen={true}
          onClose={() => setShowSavePresetModal(false)}
          onSaved={handleSaved}
          isAdmin={isAdmin}
        />
      )}

      {showLoadPresetModal && (
        <LoadPresetModal
          isOpen={true}
          onClose={() => setShowLoadPresetModal(false)}
          onPresetSelected={handlePresetSelected}
        />
      )}

      <SavedMapModal
        isOpen={savedResult !== null}
        url={savedResult?.url ?? ''}
        name={savedResult?.name}
        onClose={() => setSavedResult(null)}
      />
    </div>
  );
};
